/**
 * Where each heading sits in the document, so the outline can be navigated.
 *
 * POSITIONS COME FROM THE EDITOR, NUMBERS COME FROM outline.ts. A heading's number is its position
 * among other headings -- computed, never stored -- and that rule already has one implementation
 * which the renderer mirrors. This adds the only thing a pane needs that the rule does not carry:
 * where in the document to put the caret when somebody clicks.
 *
 * NOT ANCHORS. A heading has a stable anchor only once something points at it -- a cross-reference
 * or a bookmark creates one -- so navigating by anchor would work for the headings somebody had
 * already referenced and silently fail for the rest.
 *
 * TOP LEVEL ONLY, the same rule outlineOf follows: a heading inside a table cell or a list item is
 * not a section of the procedure.
 */

export type OutlinePlace = {
    /** The document position of the heading node. */
    pos: number;
    level: number;
    text: string;
};

/** Every top-level heading, in order, with where it is. */
export function placesOf(doc: any): OutlinePlace[] {
    const places: OutlinePlace[] = [];
    if (!doc?.forEach) return places;

    let pos = 0;
    doc.forEach((node: any) => {
        if (node?.type?.name === "heading") {
            places.push({
                pos,
                level: Number(node.attrs?.level) || 1,
                text: (node.textContent ?? "").trim(),
            });
        }
        pos += node.nodeSize;
    });
    return places;
}

/**
 * Which heading the caret is under: the last one at or before it.
 *
 * -1 WHEN THE CARET IS ABOVE THE FIRST HEADING, which is a real place to be -- a document can open
 * with a paragraph -- and is not the same as "the first section".
 */
export function currentPlace(places: OutlinePlace[], selectionFrom: number): number {
    let found = -1;
    places.forEach((place, index) => {
        if (place.pos <= selectionFrom) found = index;
    });
    return found;
}
